/**
 * Login.gov / IDM often put a second street line inside `address_line_1`
 * separated by a newline, and leave `address_line_2` empty. EUA already
 * sends two columns. Split only when line 2 is empty so EUA is unchanged.
 */

/** Matches entity-manager `from-idm` address line caps. */
export const STREET_ADDRESS_LINE_MAX_LENGTH = 255;

export interface StreetAddressLines {
  addressLine1?: string;
  addressLine2?: string;
}

function capStreetLine(value: string): string {
  return value.length <= STREET_ADDRESS_LINE_MAX_LENGTH ? value : value.slice(0, STREET_ADDRESS_LINE_MAX_LENGTH);
}

function collapseNewlinesToSpace(value: string): string {
  return value
    .replace(/\r?\n+/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
}

/**
 * If `streetLine1` contains a newline and `streetLine2` is empty, first line
 * becomes Line 1 and remaining non-empty lines become Line 2 (joined with a
 * space — Line 2 is also a single-line field).
 *
 * When Line 2 is already set, both values are trimmed and returned as-is
 * except newlines inside Line 1 are collapsed to spaces so they cannot land
 * in a single-line form field.
 */
export function splitStreetAddressLines(
  streetLine1: string | null | undefined,
  streetLine2?: string | null | undefined,
): StreetAddressLines {
  const existingLine2 = typeof streetLine2 === 'string' ? streetLine2.trim() : '';

  if (existingLine2.length > 0) {
    const rawLine1 = typeof streetLine1 === 'string' ? streetLine1.trim() : '';
    const line1 = /\r?\n/.test(rawLine1) ? collapseNewlinesToSpace(rawLine1) : rawLine1;

    return {
      ...(line1.length > 0 ? { addressLine1: capStreetLine(line1) } : {}),
      addressLine2: capStreetLine(existingLine2),
    };
  }

  if (typeof streetLine1 !== 'string') {
    return {};
  }

  const parts = streetLine1
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return {};
  }

  if (parts.length === 1) {
    return { addressLine1: capStreetLine(parts[0]) };
  }

  return {
    addressLine1: capStreetLine(parts[0]),
    addressLine2: capStreetLine(parts.slice(1).join(' ')),
  };
}
